import './DashboardPages.css';
import DashboardLayout from '../components/DashboardLayout';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationsApi } from '../api/organizations';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../api/users';
import { plansApi } from '../api/plans';
import PlanCreateModal from '../components/plans/PlanCreateModal';
import TeamRoles from '../components/teams/TeamRoles';
import { teamsApi } from '../api/teams';

function TeamDetail() {
	const [team, setTeam] = useState(null);
	const [members, setMembers] = useState([]);
	const [plans, setPlans] = useState([]);
	const { teamId } = useParams();
	const { orgData } = useAuth();
	const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
	const navigate = useNavigate();
	const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);

	useEffect(() => {
		const fetchTeamDetails = async () => {
			try {
				const teamData = await teamsApi.getTeam(teamId);
				setTeam(teamData.team);
				setMembers(teamData.team.members || []);
			} catch (error) {
				console.error('Error fetching team details:', error);
			}
		};

		const fetchPlans = async () => {
			try {
				const response = await plansApi.getTeamPlans(teamId);
				if (response && response.plans) {
					setPlans(response.plans);
				} else {
					console.error(
						'Invalid response when fetching plans:',
						response
					);
				}
			} catch (error) {
				console.error('Error fetching plans:', error);
			}
		};

		if (orgData?.id && teamId) {
			fetchTeamDetails();
			fetchPlans();
		}
	}, [teamId, orgData]);

	const handleStatusToggle = async (member) => {
		try {
			// Get the current status directly from the member object
			const currentStatus = member.TeamUsers?.isActive;

			const response = await usersApi.updateTeamUserStatus(
				team.id,
				member.id,
				!currentStatus // toggle whatever the current status is
			);

			setTeam((prevTeam) => ({
				...prevTeam,
				members: prevTeam.members.map((m) => {
					if (m.id === member.id) {
						return {
							...m,
							TeamUsers: {
								...m.TeamUsers,
								isActive: response.teamUser.isActive, // toggle the current status
							},
						};
					}
					return m;
				}),
			}));
		} catch (error) {
			console.error('Error updating user status:', error);
		}
	};

	const handlePlanCreate = async (values) => {
		try {
			if (!team || !orgData) {
				throw new Error('Team or organization data is missing');
			}

			const planData = {
				...values,
				teamId: team.id,
				organizationId: orgData.id, // Make sure we're using orgData from context
			};

			const response = await plansApi.create(planData);
			setPlans((prevPlans) => [...prevPlans, response.plan]);

			setIsPlanModalOpen(false);
		} catch (error) {
			console.error('Error creating plan:', error);
			throw error;
		}
	};

	const fetchAvailableUsers = async () => {
		try {
			const response = await usersApi.getAll(orgData.id);
			// Filter out users who are already in the team
			const existingMemberIds = members.map((member) => member.id);
			const availableUsers = response.users.filter(
				(user) => !existingMemberIds.includes(user.id)
			);
			setAvailableUsers(availableUsers);
		} catch (error) {
			console.error('Error fetching available users:', error);
		}
	};

	const handleAddMembers = async () => {
		try {
			// Transform selectedUsers array into the format expected by the backend
			const usersPayload = selectedUsers.map((userId) => ({
				userId,
				isTeamLead: false, // Default to false for new members
			}));

			await organizationsApi.teams.addMembers(
				orgData.id,
				teamId,
				{ users: usersPayload } // Wrap in users object as expected by backend
			);

			// Refresh team members
			const teamData = await organizationsApi.teams.getOne(
				orgData.id,
				teamId
			);
			setMembers(teamData.team.members);

			// Reset and close modal
			setIsAddMemberModalOpen(false);
			setSelectedUsers([]);
		} catch (error) {
			console.error('Error adding team members:', error);
			alert(error.message || 'Failed to add team members');
		}
	};

	const handleTeamUpdate = (updatedTeam) => {
		setTeam(updatedTeam);
		setMembers(updatedTeam.members || []);
	};

	if (!team) return <div>Loading...</div>;

	const teamLead = members.find((member) => member.TeamUsers?.isTeamLead);
	const teamMembers = members.filter(
		(member) => !member.TeamUsers?.isTeamLead
	);

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<h1>{team?.name}</h1>
				<div className='content-section'>
					{teamLead && (
						<div className='detail-card team-lead-card'>
							<h2>Team Lead</h2>
							<div className='team-lead-info'>
								{teamLead.userPhoto ? (
									<img
										src={teamLead.userPhoto}
										alt={`${teamLead.firstName} ${teamLead.lastName}`}
										className='member-avatar'
									/>
								) : (
									<div className='member-avatar default-avatar'>
										<span className='material-icons'>
											person
										</span>
									</div>
								)}
								<div>
									<h3>{`${teamLead.firstName} ${teamLead.lastName}`}</h3>
									<p>{teamLead.phoneNumber}</p>
									<p>{teamLead.email}</p>
								</div>
							</div>
						</div>
					)}

					<TeamRoles
						team={team}
						onTeamUpdate={handleTeamUpdate}
					/>

					<div className='detail-card'>
						<div className='section-header'>
							<h2>Team Members</h2>
							<button
								className='primary-button'
								onClick={() => {
									fetchAvailableUsers();
									setIsAddMemberModalOpen(true);
								}}
								style={{
									'--primary-color':
										orgData?.primaryColor || '#4b95a3',
								}}>
								<span className='material-icons'>
									person_add
								</span>
								Add Members
							</button>
						</div>
						<div className='members-grid'>
							{teamMembers.map((member) => (
								<div
									key={member.id}
									className='member-card'
									style={{
										'--primary-color':
											orgData?.primaryColor || '#4b95a3',
										'--primary-color-light': `${
											orgData?.primaryColor || '#4b95a3'
										}1A`,
									}}>
									<div className='member-photo'>
										{member.userPhoto ? (
											<img
												src={member.userPhoto}
												alt={`${member.firstName} ${member.lastName}`}
												className='member-avatar'
											/>
										) : (
											<div className='member-avatar default-avatar'>
												<span className='material-icons'>
													person
												</span>
											</div>
										)}
									</div>
									<div className='member-details'>
										<h3>{`${member.firstName} ${member.lastName}`}</h3>
										<p>{member.phoneNumber}</p>
										<p>
											Status:{' '}
											{member.TeamUsers?.isActive
												? 'Active'
												: 'Inactive'}
										</p>
										<button
											onClick={() =>
												handleStatusToggle(member)
											}
											className='status-toggle-btn'
											disabled={!member.TeamUsers}>
											{member.TeamUsers?.isActive
												? 'Set As Inactive'
												: 'Set As Active'}
										</button>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className='detail-card'>
						<div className='section-header'>
							<h2>Plans</h2>
							<button
								className='primary-button'
								onClick={() => setIsPlanModalOpen(true)}
								style={{
									'--primary-color':
										orgData?.primaryColor || '#4b95a3',
								}}>
								<span className='material-icons'>add</span>
								Add Plan
							</button>
						</div>
						<div className='plans-grid'>
							{plans.map((plan) => (
								<div
									key={plan.id}
									className='dashboard-card'
									onClick={() => {
										navigate(`/plans/${plan.id}`, {
											state: {
												teamMembers: members,
												roles: team.roles,
											},
										});
									}}
									style={{
										'--primary-color':
											orgData?.primaryColor || '#4b95a3',
										'--primary-color-light': `${
											orgData?.primaryColor || '#4b95a3'
										}1A`,
									}}>
									<span className='material-icons card-icon'>
										event
									</span>
									<h3>{plan.mainTitle}</h3>
									{plan.description && (
										<p className='plan-description'>
											{plan.description}
										</p>
									)}
									<div className='plan-meta'>
										{plan.estimatedDuration && (
											<span className='duration'>
												<span className='material-icons'>
													schedule
												</span>
												{plan.estimatedDuration} min
											</span>
										)}
										<span
											className={`status status-${plan.status}`}>
											{plan.status}
										</span>
									</div>
								</div>
							))}
							{plans.length === 0 && (
								<div className='no-plans-message'>
									No plans available
								</div>
							)}
						</div>
					</div>
				</div>

				<PlanCreateModal
					visible={isPlanModalOpen}
					onClose={() => setIsPlanModalOpen(false)}
					onSubmit={handlePlanCreate}
					teamId={team?.id}
				/>

				{isAddMemberModalOpen && (
					<div className='modal'>
						<div className='modal-content'>
							<div className='modal-header'>
								<h2>Add Team Members</h2>
								<button
									className='close-button'
									onClick={() =>
										setIsAddMemberModalOpen(false)
									}>
									<span className='material-icons'>
										close
									</span>
								</button>
							</div>
							<div className='modal-body'>
								{availableUsers.map((user) => (
									<div
										key={user.id}
										className='user-selection-item'>
										<input
											type='checkbox'
											id={`user-${user.id}`}
											checked={selectedUsers.includes(
												user.id
											)}
											onChange={(e) => {
												if (e.target.checked) {
													setSelectedUsers([
														...selectedUsers,
														user.id,
													]);
												} else {
													setSelectedUsers(
														selectedUsers.filter(
															(id) =>
																id !== user.id
														)
													);
												}
											}}
										/>
										<label htmlFor={`user-${user.id}`}>
											{user.firstName} {user.lastName} (
											{user.email})
										</label>
									</div>
								))}
							</div>
							<div className='modal-footer'>
								<button
									className='secondary-button'
									onClick={() =>
										setIsAddMemberModalOpen(false)
									}>
									Cancel
								</button>
								<button
									className='primary-button'
									onClick={handleAddMembers}
									disabled={selectedUsers.length === 0}>
									Add Selected Members
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</DashboardLayout>
	);
}

export default TeamDetail;
